import { BandAnalyser } from './analyser.js';

// Owns the WebAudio graph and feeds the engine via BandAnalyser. Each select*
// method runs synchronously up to the first await so iOS Safari unlocks the
// AudioContext within the user gesture.
export class SourceManager {
  constructor({ engine, demoUrl = '/digable-planets/assets/cathedral-demo.wav' } = {}) {
    this.engine = engine;
    this.demoUrl = demoUrl;
    this.ctx = null;
    this.analyserNode = null;
    this.bandAnalyser = null;
    this.currentSrc = null;
    // Set during async source loads. Guards against stacked BufferSources
    // (e.g. a second Demo click before the first decode finishes) which
    // play the same buffer slightly out of phase — audible as a low hum.
    this._loading = false;
  }

  // Internal: wraps an async loader with an in-flight guard. A second call
  // while one is in flight is rejected with a typed error the UI surfaces.
  async _withLoadingGuard(fn) {
    if (this._loading) {
      const err = new Error('Source change in progress — wait for it to finish');
      err.name = 'AlreadyLoading';
      throw err;
    }
    this._loading = true;
    try { return await fn(); }
    finally { this._loading = false; }
  }

  _unlock() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      const node = this.ctx.createAnalyser();
      node.fftSize = 256;
      node.smoothingTimeConstant = 0.6;
      this.analyserNode = node;
      this.bandAnalyser = new BandAnalyser(node);
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  _stopCurrent() {
    const cur = this.currentSrc;
    if (!cur) return;
    if (cur.type === 'buffer' && cur.node) {
      try { cur.node.stop(); } catch (_) { /* may already be stopped */ }
    }
    if (cur.stream) {
      for (const track of cur.stream.getTracks()) {
        try { track.stop(); } catch (_) { /* ignore */ }
      }
    }
    if (cur.node && typeof cur.node.disconnect === 'function') {
      try { cur.node.disconnect(); } catch (_) { /* ignore */ }
    }
    this.currentSrc = null;
  }

  _attach(name) {
    this.engine.setAnalyser(this.bandAnalyser);
    this.engine.setSource(name);
  }

  _fail(err, fallbackMsg) {
    const msg = (err && err.message) ? err.message : fallbackMsg;
    this.engine.setError(msg);
    this.engine.setSource('none');
    throw err;
  }

  // True while an async source load is in flight. The picker uses this
  // to disable buttons so the user can't queue a stacked playback.
  get isLoading() { return this._loading; }

  async selectDemo() {
    return this._withLoadingGuard(async () => {
      this._unlock();
      this._stopCurrent();
      try {
        const res = await fetch(this.demoUrl);
        if (!res.ok) throw new Error(`Demo fetch failed: ${res.status}`);
        const buf = await res.arrayBuffer();
        const audioBuf = await this.ctx.decodeAudioData(buf);
        const node = this.ctx.createBufferSource();
        node.buffer = audioBuf;
        node.loop = true;
        node.connect(this.analyserNode);
        node.connect(this.ctx.destination);
        node.start();
        this.currentSrc = { type: 'buffer', node, name: 'demo track' };
        this._attach('demo');
      } catch (err) {
        this._fail(err, 'Failed to load demo audio');
      }
    });
  }

  async selectFile(file) {
    return this._withLoadingGuard(async () => {
      this._unlock();
      this._stopCurrent();
      try {
        const buf = await file.arrayBuffer();
        const audioBuf = await this.ctx.decodeAudioData(buf);
        const node = this.ctx.createBufferSource();
        node.buffer = audioBuf;
        node.loop = false;
        node.connect(this.analyserNode);
        node.connect(this.ctx.destination);
        node.start();
        this.currentSrc = { type: 'buffer', node, name: file.name };
        this._attach('file');
      } catch (err) {
        this._fail(err, 'Failed to decode audio file');
      }
    });
  }

  // Toggle: a second Mic click while the mic is the current source stops it.
  async selectMic() {
    if (this.currentSrc && this.currentSrc.name === 'mic') {
      this._stopCurrent();
      this.engine.setSource('none');
      return;
    }
    return this._withLoadingGuard(async () => {
      this._unlock();
      this._stopCurrent();
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Microphone API not available. Use HTTPS and a modern browser.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        const node = this.ctx.createMediaStreamSource(stream);
        node.connect(this.analyserNode);
        this.currentSrc = { type: 'stream', node, stream, name: 'mic' };
        this._attach('mic');
      } catch (err) {
        const msg = _micErrorMessage(err);
        this.engine.setError(msg);
        this.engine.setSource('none');
        const wrapped = new Error(msg);
        wrapped.name = err && err.name ? err.name : 'MicrophoneError';
        throw wrapped;
      }
    });
  }

  async selectTab() {
    return this._withLoadingGuard(async () => {
      this._unlock();
      this._stopCurrent();
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
        const msg = 'Tab audio unsupported in this browser';
        this.engine.setError(msg);
        this.engine.setSource('none');
        throw new Error(msg);
      }
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        const audioTracks = stream.getAudioTracks();
        for (const v of stream.getVideoTracks()) {
          try { v.stop(); } catch (_) { /* ignore */ }
        }
        if (audioTracks.length === 0) {
          throw new Error("Tab share has no audio track — try 'Share tab audio' checkbox");
        }
        const audioStream = new MediaStream([audioTracks[0]]);
        const node = this.ctx.createMediaStreamSource(audioStream);
        node.connect(this.analyserNode);
        this.currentSrc = { type: 'stream', node, stream: audioStream, name: 'tab' };
        this._attach('tab');
      } catch (err) {
        this._fail(err, 'Tab capture failed');
      }
    });
  }

  // Tear down everything: stop the active source, disconnect the analyser
  // node, close the AudioContext. ctx.close() is what actually releases the
  // macOS audio session — without it the session can stay open across page
  // reloads, leaking any active MediaStream into the next page load.
  stop() {
    this._stopCurrent();
    if (this.analyserNode) {
      try { this.analyserNode.disconnect(); } catch (_) { /* ignore */ }
    }
    if (this.ctx && this.ctx.state !== 'closed') {
      try { this.ctx.close(); } catch (_) { /* ignore */ }
    }
    this.ctx = null;
    this.analyserNode = null;
    this.bandAnalyser = null;
    this.engine.stop();
  }
}

// Map a getUserMedia error to user-actionable copy. The DOMException name
// is the stable cross-browser identifier; .message is browser-specific.
function _micErrorMessage(err) {
  if (!err) return 'Microphone unavailable';
  switch (err.name) {
    case 'NotAllowedError':
      // NotAllowedError fires for site-level denial AND OS-level denial AND
      // dismissed prompts. Browsers do not distinguish — message points users
      // to every checkpoint.
      return 'Mic access not granted — check the URL-bar camera icon AND macOS Settings → Privacy & Security → Microphone for your browser. If both look fine, fully quit and restart the browser.';
    case 'NotFoundError':
      return 'No microphone found — check your system audio input devices';
    case 'NotReadableError':
      return 'Microphone is in use by another app (Zoom / Slack / Discord / etc.)';
    case 'OverconstrainedError':
      return 'Microphone constraints not supported by your device';
    case 'SecurityError':
      return 'Microphone blocked — page must be served over HTTPS';
    default:
      return err.message || `Microphone error: ${err.name || 'unknown'}`;
  }
}
